import { toast } from 'sonner';
import { useRouter } from 'next/navigation';
import { UrlPaths } from '../../lib/constants';
import { useMutation } from '../../hooks/useMutation';
import { APIFetch } from '../../lib/fetch';
import {
  AcceptTermsMutationPayload,
  PersistCognitoTokenPayload,
  SignInWithEmailPasswordMutationPayload,
  SignOutMutationPayload,
} from './types';
import useCsrfToken from '../../hooks/use-csrf-token-header';
import { getHostURL, isCognitoEnabled } from '../../lib/env';
import { useSWRConfig } from 'swr';
import { useClearMessages } from '../messages/hooks';
import HttpStatusCode from '../../lib/http-status-code.enum';
import { useUserSession } from '../user/hooks';
import useCognito from '../../hooks/use-cognito';

const CSRF_TOKEN_HEADER = 'x-csrf-token';

export function useSignOut() {
  const router = useRouter();
  const csrfToken = useCsrfToken();
  const session = useUserSession();
  const clearMessages = useClearMessages();
  const { signOut: cognitoSignOut } = useCognito();

  const { mutate: swrMutate } = useSWRConfig();

  const logoutUrl = '/api/auth/logout';

  const clearCache = async () => {
    fetch.authorize();
    clearMessages();
    session.set(null);
    await swrMutate(() => true, undefined, { revalidate: false });
  };

  const fetch = new APIFetch(getHostURL()).addHeaders({
    [CSRF_TOKEN_HEADER]: csrfToken,
  });

  const mutation = useMutation<SignOutMutationPayload, {}>(
    logoutUrl,
    fetch.post,
    {
      onSuccess: async () => {
        if (isCognitoEnabled) {
          await clearCache();
          await cognitoSignOut();
          return;
        }

        router.push(UrlPaths.SIGN_IN);
        await clearCache();
      },
      onError: (error) => {
        console.error(error);
        toast.error('There was an error signing out.');
      },
    }
  );

  const mutate = () => {
    mutation.mutate({});
  };

  return { mutate };
}

export function useSignInWithEmailPassword() {
  const csrfToken = useCsrfToken();

  const loginUrl = '/api/auth/login';

  const fetch = new APIFetch(getHostURL()).addHeaders({
    [CSRF_TOKEN_HEADER]: csrfToken,
  });

  return useMutation<SignInWithEmailPasswordMutationPayload, {}>(
    loginUrl,
    fetch.post,
    {
      onSuccess: () => {
        if (typeof window !== 'undefined') {
          window.location.assign(UrlPaths.HOME);
        }
      },
      onError: (error) => {
        console.error(error);
        const message =
          error.status === HttpStatusCode.Unauthorized
            ? 'We can’t find an account matching the username and password you entered. Please check your username and password and try again.'
            : 'There was an error signing in.';

        toast.error(error.info.message || message, {
          id: 'auth-error-message',
        });
      },
    }
  );
}

export function usePersistCognitoToken() {
  const csrfToken = useCsrfToken();

  const url = '/api/auth/cognito';

  const fetch = new APIFetch(getHostURL()).addHeaders({
    [CSRF_TOKEN_HEADER]: csrfToken,
  });

  return useMutation<PersistCognitoTokenPayload, {}>(url, fetch.post, {
    onSuccess: () => {
      if (typeof window !== 'undefined') {
        window.location.assign(UrlPaths.HOME);
      }
    },
    onError: (error) => {
      toast.error(error.info?.message || "There was an error signing in.", {
        id: 'auth-error-message',
      });
    },
  });
}

export function useAcceptTermsOfUse() {
  const csrfToken = useCsrfToken();
  const router = useRouter();

  const acceptTermsOfUseUrl = '/api/onboarding/terms-of-use';

  const fetch = new APIFetch(getHostURL()).addHeaders({
    [CSRF_TOKEN_HEADER]: csrfToken,
  });

  return useMutation<AcceptTermsMutationPayload, {}>(
    acceptTermsOfUseUrl,
    fetch.post,
    {
      onSuccess: () => {
        router.push(UrlPaths.HOME);
      },
      onError: (error) => {
        console.log(error);
        toast.error('We have encountered an error while trying to accept the terms of service and privacy policy.');
      },
    }
  );
}
